import { LOCAL_STORAGE_KEY } from 'constants';
import React, { useCallback, useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useTeamColors } from 'hooks';
import { AuthContainer } from 'components';
import { getSiteIconsWithFallback } from 'utils/pwaIcons';
import { detectInAppWebView } from 'utils/browserDetection';
import {
  TopNavBar,
  LeftNavContainer,
  TournamentLink,
  MobileOnlyText,
  DesktopOnlyText,
  SiteNameContainer,
  SiteIcon,
  SiteName,
  NavIconsContainer,
} from './style';

/**
 * Top navigation bar component that displays site name and uses team colors
 * Matches the Flutter app design with team-colored background
 */
const TopNav = () => {
  const { data: configuration } = useSelector((state) => state.configuration);
  const { isUserAuthEnabled } = useSelector((state) => state.user);
  const { mainColor } = useTeamColors(configuration);
  const isUserAuth = !!useSelector((state) => state.user.data.token);
  const shouldDisplayAuth = isUserAuthEnabled && !isUserAuth;

  // CRITICAL: No default - this codebase serves 100+ sites
  const siteName = configuration?.site_name || '';
  const siteUrl = configuration?.site_url || '';

  // State for site icon with fallback
  const [siteIconUrl, setSiteIconUrl] = useState(
    '/icons/default/apple-touch-icon.png'
  );

  // Check if we're in an in-app webview
  const { isInAppWebView } = detectInAppWebView();

  // Check if this is MadnessTime site
  const isMadnessTime = siteUrl.toLowerCase().includes('madnesstime');

  // Check if this is a PlayoffsTime site: playoffsti.me (NBA) or
  // playoffti.me (MLB) - both link to the same playoffs bracket app
  const isMlbPlayoffsTime = siteUrl.toLowerCase().includes('playoffti.me');
  const isPlayoffsTime =
    siteUrl.toLowerCase().includes('playoffsti') || isMlbPlayoffsTime;

  // Check if this is a tournament site (Madness Time or Playoffs Time)
  const isTournamentSite = isMadnessTime || isPlayoffsTime;

  // Get site-specific icon with fallback logic
  useEffect(() => {
    const loadSiteIcon = async () => {
      if (configuration) {
        try {
          const icons = await getSiteIconsWithFallback(configuration);
          setSiteIconUrl(icons.appleTouchIcon); // Use apple-touch-icon for best quality
          console.log(
            '[TopNav] Site icon loaded with fallback:',
            icons.appleTouchIcon
          );
        } catch (error) {
          console.error('[TopNav] Error loading site icon:', error);
          setSiteIconUrl('/icons/default/apple-touch-icon.png');
        }
      }
    };

    loadSiteIcon();
  }, [configuration]);

  // Check if this is a college team site
  const isCollegeTeamSite =
    (configuration?.display_one_team === '1' ||
      configuration?.display_one_team === 1) &&
    configuration?.sport_level === 'Collegiate';

  const handleTitleClick = useCallback(() => {
    console.log(
      '[TopNav] Title clicked, isCollegeTeamSite:',
      isCollegeTeamSite
    );

    if (isCollegeTeamSite) {
      // Get the last selected college league from localStorage
      const lastCollegeLeagueKey = `${LOCAL_STORAGE_KEY}last-college-league`;
      const savedLeague = localStorage.getItem(lastCollegeLeagueKey);

      console.log('[TopNav] Saved college league:', savedLeague);

      if (savedLeague) {
        try {
          const { label, value } = JSON.parse(savedLeague);
          console.log('[TopNav] Reloading with saved league:', label, value);
          // Use window.location for full page reload to ensure proper state reset
          window.location.href = `/?collegeLeague=${encodeURIComponent(label)}&collegeLeagueId=${value}`;
          return;
        } catch (e) {
          console.error('[TopNav] Error parsing saved league:', e);
        }
      }

      // If no saved league, reload home (will trigger default "Upcoming Events")
      console.log('[TopNav] No saved league, reloading home');
      window.location.href = '/';
    } else {
      // For non-college sites, reload home page to refresh data
      console.log('[TopNav] Non-college site, reloading home');
      window.location.href = '/';
    }
  }, [isCollegeTeamSite]);

  return (
    <TopNavBar $backgroundColor={mainColor}>
      <LeftNavContainer>
        {isTournamentSite && !isInAppWebView ? (
          <TournamentLink
            href={
              isMadnessTime
                ? '/tournament/madness'
                : // ?league= opens the site's own sport in the bracket app
                  `/tournament/playoffs/?league=${isMlbPlayoffsTime ? 'mlb' : 'nba'}`
            }
          >
            <MobileOnlyText>
              {isMadnessTime ? 'Tournament' : 'Playoffs'}
            </MobileOnlyText>
            <DesktopOnlyText>
              {isMadnessTime ? 'Track the Tournament' : 'Track the Playoffs'}
            </DesktopOnlyText>
          </TournamentLink>
        ) : null}
      </LeftNavContainer>
      <SiteNameContainer onClick={handleTitleClick}>
        <SiteIcon
          alt="Site icon"
          onError={(e) => {
            console.warn(
              '[TopNav] Site icon failed to load, using default:',
              siteIconUrl
            );
            e.target.src = '/icons/default/apple-touch-icon.png';
          }}
          src={siteIconUrl}
        />
        <SiteName>{siteName}</SiteName>
      </SiteNameContainer>
      <NavIconsContainer>
        {shouldDisplayAuth ? <AuthContainer /> : null}
      </NavIconsContainer>
    </TopNavBar>
  );
};

export default TopNav;
